import React, { createContext, useContext, useState, useEffect } from 'react';

const ComplianceContext = createContext();

// Helper function to load compliance state from localStorage
const loadComplianceState = (projectId) => {
  try {
    const savedState = localStorage.getItem(`compliance_${projectId}`);
    return savedState ? JSON.parse(savedState) : null;
  } catch (e) {
    console.error("Failed to load compliance state from localStorage", e);
    return null;
  }
};

// Helper function to save compliance state to localStorage
const saveComplianceState = (projectId, complianceItems) => {
  try {
    localStorage.setItem(`compliance_${projectId}`, JSON.stringify(complianceItems));
  } catch (e) {
    console.error("Failed to save compliance state to localStorage", e);
  }
};

// Helper function to load policies from localStorage
const loadPolicies = () => {
  try {
    const savedPolicies = localStorage.getItem('compliancePolicies');
    return savedPolicies ? JSON.parse(savedPolicies) : [];
  } catch (e) {
    console.error("Failed to load compliance policies from localStorage", e);
    return [];
  }
};

// Function to determine applicable policies for a project
const getApplicablePolicies = (project, allPolicies) => {
  if (!project || !allPolicies.length) return [];
  
  return allPolicies.filter(policy => {
    if (!policy.isActive) return false;
    
    const { applicableTo } = policy;
    
    // Check if no criteria are specified (applies to all projects)
    if (applicableTo.projectTypes.length === 0 && 
        applicableTo.locations.length === 0 && 
        applicableTo.donors.length === 0 && 
        applicableTo.minimumBudget === 0) {
      return true;
    }
    
    // Check project type
    if (applicableTo.projectTypes.length > 0 && 
        project.projectType && 
        !applicableTo.projectTypes.includes(project.projectType)) {
      return false;
    }
    
    // Check location
    if (applicableTo.locations.length > 0 && 
        project.location && 
        !applicableTo.locations.some(loc => project.location.includes(loc))) {
      return false;
    }
    
    // Check donor
    if (applicableTo.donors.length > 0 && 
        project.donor && 
        project.donor.name && 
        !applicableTo.donors.includes(project.donor.name)) {
      return false;
    }
    
    // Check budget
    if (applicableTo.minimumBudget > 0 && 
        project.budget && 
        project.budget < applicableTo.minimumBudget) {
      return false;
    }
    
    return true;
  });
};

export const ComplianceProvider = ({ children, projectId, project }) => {
    const [complianceItems, setComplianceItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [policies, setPolicies] = useState([]);
    const [complianceHistory, setComplianceHistory] = useState([]);

    // Initialize compliance items
    useEffect(() => {
        if (!projectId) return;

        setLoading(true);
        
        // Load policies
        const allPolicies = loadPolicies();
        setPolicies(allPolicies);
        
        // Try to load saved state from localStorage
        const savedState = loadComplianceState(projectId);
        
        if (savedState) {
            setComplianceItems(savedState.items || []);
            setComplianceHistory(savedState.history || []);
        } else if (project) {
            // Initialize with applicable policies for this project
            const applicablePolicies = getApplicablePolicies(project, allPolicies);
            const initialItems = applicablePolicies.map((policy, index) => ({
                id: `policy-${policy.id}`,
                category: policy.category,
                item: policy.title,
                description: policy.description,
                isComplete: false,
                isGeneral: true,
                policyId: policy.id
            }));
            
            setComplianceItems(initialItems);
            setComplianceHistory([{
                date: new Date().toISOString(),
                percentage: 0,
                itemsCompleted: 0,
                totalItems: initialItems.length
            }]);
        }
        
        setLoading(false);
    }, [projectId, project]);

    // Save compliance items to localStorage whenever they change
    useEffect(() => {
        if (complianceItems.length > 0 && projectId) {
            saveComplianceState(projectId, {
                items: complianceItems,
                history: complianceHistory
            });
        }
    }, [complianceItems, complianceHistory, projectId]);

    // Calculate compliance percentage
    const compliancePercentage = complianceItems.length > 0 
        ? Math.round((complianceItems.filter(item => item.isComplete).length / complianceItems.length) * 100)
        : 0;

    // Calculate compliance trend
    const complianceTrend = complianceHistory.length > 1 
        ? compliancePercentage - complianceHistory[complianceHistory.length - 2].percentage
        : 0;

    // Handle toggling compliance status
    const toggleCompliance = (itemId) => {
        setComplianceItems(prevItems => {
            const updatedItems = prevItems.map(item => 
                item.id === itemId ? { ...item, isComplete: !item.isComplete } : item
            );
            
            // Update history when compliance changes
            const newPercentage = Math.round((updatedItems.filter(item => item.isComplete).length / updatedItems.length) * 100);
            setComplianceHistory(prev => [...prev, {
                date: new Date().toISOString(),
                percentage: newPercentage,
                itemsCompleted: updatedItems.filter(item => item.isComplete).length,
                totalItems: updatedItems.length
            }]);
            
            return updatedItems;
        });
    };

    // Handle adding a new compliance item
    const addComplianceItem = (item) => {
        const newComplianceItem = {
            id: `custom-${Date.now()}`,
            category: item.category,
            item: item.description,
            description: item.description,
            isComplete: false,
            isGeneral: false
        };
        
        setComplianceItems(prevItems => [...prevItems, newComplianceItem]);
    };

    // Handle updating a compliance item
    const updateComplianceItem = (itemId, updates) => {
        setComplianceItems(prevItems => 
            prevItems.map(item => 
                item.id === itemId ? { ...item, ...updates } : item
            )
        );
    };

    // Handle deleting a compliance item
    const deleteComplianceItem = (itemId) => {
        setComplianceItems(prevItems => prevItems.filter(item => item.id !== itemId));
    };

    // Function to refresh compliance items based on current policies and project
    const refreshComplianceItems = () => {
        if (!project) return;
        
        const allPolicies = loadPolicies();
        const applicablePolicies = getApplicablePolicies(project, allPolicies);
        
        // Get existing policy-based items
        const existingPolicyItems = complianceItems.filter(item => item.isGeneral);
        const existingCustomItems = complianceItems.filter(item => !item.isGeneral);
        
        // Create new policy-based items
        const newPolicyItems = applicablePolicies.map((policy) => {
            // Check if this policy already exists in compliance items
            const existingItem = existingPolicyItems.find(item => item.policyId === policy.id);
            
            if (existingItem) {
                return existingItem;
            }
            
            return {
                id: `policy-${policy.id}`,
                category: policy.category,
                item: policy.title,
                description: policy.description,
                isComplete: false,
                isGeneral: true,
                policyId: policy.id
            };
        });
        
        // Combine new policy items with existing custom items
        setComplianceItems([...newPolicyItems, ...existingCustomItems]);
    };

    return (
        <ComplianceContext.Provider value={{
            complianceItems,
            loading,
            compliancePercentage,
            complianceTrend,
            complianceHistory,
            toggleCompliance,
            addComplianceItem,
            updateComplianceItem,
            deleteComplianceItem,
            refreshComplianceItems,
            policies
        }}>
            {children}
        </ComplianceContext.Provider>
    );
};

export const useCompliance = () => {
    const context = useContext(ComplianceContext);
    if (!context) {
        throw new Error('useCompliance must be used within a ComplianceProvider');
    }
    return context;
};